import { isEqual } from "lodash";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import MdaeDataGrid, {
  IMdaeDataGridProps,
} from "../../common_components/common_widgets/data-grid";
import MdaeButton from "../../common_components/common_widgets/button";
import CaseDetail from "./case-detail/component";
import Guid from "devextreme/core/guid";
import { SET_ESMD_SELECTED_TAB_INDEX } from "../../common_components/case-details/esMd-dashboard/reducers/esmd-submission-reducer";
import { toggleCaseDetailPopup } from "./case-detail/case-detail-common-functions";
export const getDistinctStringsFromCommaSeperatedList = (commaSeperatedString) => {
  const stringArray = commaSeperatedString?.split(",");
  if (stringArray?.length > 0) {
    //get distinct numbers
    const distinctSet = new Set(stringArray);
    return Array.from(distinctSet).join(",");
  }
  return commaSeperatedString;
}
const EsmdAuditDashboardTab: React.FC<any> = (props) => {
  const dispatch = useDispatch();
  const esmdSubmissionListForAudit = useSelector(
    (state) => state["externalAuditReducer"].esmdSubmissionListForAudit,
    isEqual
  );
  const isCaseDetailPopupVisible = useSelector(
    (state: any) => state["caseDetailReducer"].isCaseDetailPopupVisible,
    isEqual
  );
  const renderDataGridCustomButtonsBeforeTemplate = () => {
    return (
      <div className="submission-text">
        Most Recent Submission Status by Case:
      </div>
    );
  };
  const onEsmdPreviewClick = (cellData) => {
    let data = {
      caseId: cellData.caseId,
      caseDetailId: cellData.caseDetailId,
      eaCaseId: cellData.eaCaseId,
      claimType:
        cellData.claimTypeCd?.toUpperCase() == "I"
          ? "HOSPITAL"
          : "PROFESSIONAL",
      visible: true,
      auditCaseEntryTypeCd: cellData.auditCaseEntryTypeCd
    };
    toggleCaseDetailPopup(dispatch, true, "Audit Case Details", data, 5);
    dispatch({
      type: SET_ESMD_SELECTED_TAB_INDEX,
      data: {
        esmdSelectedTabIndex: 1,
        isTabChangedProgrammatically: true,
      },
    });
  };
  const esmdAuditLevelDetailsGridOptions = useMemo(() => {
    return getEsmdAuditLevelDetailsGridOption(
      esmdSubmissionListForAudit,
      onEsmdPreviewClick
    );
  }, [esmdSubmissionListForAudit]);

  return (
    <div className="esmd-submission-grid-container">
      <MdaeDataGrid
        {...esmdAuditLevelDetailsGridOptions}
        mdaeDataGridCustomButtonsBefore={
          renderDataGridCustomButtonsBeforeTemplate
        }
      ></MdaeDataGrid>
      {isCaseDetailPopupVisible && (
        <CaseDetail
          eaRoundId={0}
          caseMyTaskWidgetId={new Guid().toString()}
          caseMyPastDueTaskDrawerId={new Guid().toString()}
          caseMyThisWeekTaskDrawerId={new Guid().toString()}
          caseMyUpcomingTaskDrawerId={new Guid().toString()}
          caseMyCompletedTaskDrawerId={new Guid().toString()}
          isViewOnly={false}
          isHideBillingHistoryLink={false}
          isFromDenialPredictor={false}
        ></CaseDetail>
      )}
    </div>
  );
};
const getEsmdAuditLevelDetailsGridOption = (
  esmdSubmissionListForAudit,
  onEsmdPreviewClick
) => {
  const documentListDataGridOptions: IMdaeDataGridProps = {
    id: "grdEsmdAuditLevelDetails",
    allowColumnResizing: true,
    mdaeColumnChooser: { isMdaeColumnChooserVisible: true },
    mdaeSearchPanel: { visible: true },
    clearFilterButtonId: "btn-esmd-audit-level-details-grid-clear-filter",
    dataSource: esmdSubmissionListForAudit,
    filterRow: { visible: true },
    headerFilter: { visible: true },
    pageSize: 10,

    mdaeColumns: [
      {
        fixed: true,
        allowSorting: false,
        allowFiltering: false,
        allowGrouping: false,
        allowResizing: false,
        allowReordering: false,
        allowHiding: false,
        allowExporting: false,
        dataField: "esmdDocumentUploadId",
        caption: "",
        alignment: "center",
        width: 50,
        visible: true,
        cellRender: (cellData) => {
          return (
            <div>
              <i
                id="btnPreviewEsmdSubmission"
                className={"preview-gray-icon esmd-preview-gray-icon"}
                onClick={(e) => {
                  onEsmdPreviewClick(cellData?.data);
                }}
              ></i>
            </div>
          );
        },
      },

      {
        dataField: "patientAcctNo",
        caption: "Account Number",
        allowFiltering: true,
        allowSorting: true,
        width: 150,
      },
      {
        dataField: "patientFullName",
        caption: "Patient Name",
        allowFiltering: true,
        allowSorting: true,
        width: 120,
      },
      {
        dataField: "patientMRN",
        caption: "MRN",
        allowFiltering: true,
        allowSorting: true,
        width: 120,
      },
      {
        dataField: "documentCount",
        caption: "Files Sent",
        allowFiltering: true,
        allowSorting: true,
        width: 120,
      },
      {
        dataField: "submissionStatus",
        caption: "CMS Status",
        dataType: "date",
        format: "MM/dd/yyyy HH:mm",
        allowFiltering: true,
        allowSorting: true,
        width: 150,
      },
      {
        dataField: "cmsTrackingNumbers",
        caption: "CMS Tracking ID",
        width: 130,
        cellRender(e) {
          let trackingNumberString = getDistinctStringsFromCommaSeperatedList(
            e.data.cmsTrackingNumbers
          );
          return <div title={trackingNumberString}>{trackingNumberString}</div>;
        },
      },
      {
        dataField: "npi",
        caption: "NPI",
        allowFiltering: true,
        allowSorting: true,
        width: 120,
      },
      {
        dataField: "provider",
        caption: "Provider",
        allowFiltering: true,
        allowSorting: true,
        width: 250,
      },
      {
        dataField: "eaContentTypeCode",
        caption: "Content Type",
        allowFiltering: true,
        allowSorting: true,
        width: 250,
      },
      {
        dataField: "referenceId",
        caption: "Reference ID",
        allowFiltering: true,
        allowSorting: true,
        width: 150,
      },
      {
        dataField: "payerCaseId",
        caption: "Payer Case/Claim ID",
        allowFiltering: true,
        allowSorting: true,
        sortOrder: "desc",
        width: 200,
      },
      {
        dataField: "requester",
        caption: "Requester",
        allowFiltering: true,
        allowSorting: true,
        width: 250,
      },
      {
        dataField: "reviewContractorCode",
        caption: "Review Contractor",
        allowFiltering: true,
        allowSorting: true,
        width: 250,
      },
    ],
  };
  return documentListDataGridOptions;
};

export default EsmdAuditDashboardTab;
